import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator, KeyboardAvoidingView, ScrollView, type ScrollView as ScrollViewType } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useApiClient, extractErrorMessage, isRateLimitError } from '../../../helpers/apiClient.helper';
import { useDialog } from '../../../src/shared/hooks/useDialog';
import { sanitizeInput } from '../../../src/helpers/sanitizeInput.helper';
import { GROUP_NAME_MIN_LENGTH, GROUP_NAME_MAX_LENGTH } from '../../../src/friends/constants/groupName';
import {
  WORLD_CUP_PAYOUT_MODE,
  WORLD_CUP_GROUP_DESCRIPTION_MAX_LENGTH,
} from '../../../src/worldcup/worldcup.constants';
import type { WorldCupPayoutMode } from '../../../src/worldcup/worldcup.constants';
import { createWorldCupCompetitionGroup } from '../../../src/worldcup/helpers/worldCupCompetitionGroupsApi';
import { useCompetitionId, useCompetitionBasePath } from '../../../src/competitions/contexts/CompetitionContext';
import { competitionGroupsRoute } from '../../../src/competitions/helpers/competitionRoutes.helper';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { CreateWorldCupGroupStep1Form } from './CreateWorldCupGroupStep1Form';
import { CreateWorldCupGroupStep2Form } from './CreateWorldCupGroupStep2Form';
import { getCreateWorldCupGroupScreenStyles } from './CreateWorldCupGroupScreen.styles';
import {
  CREATE_WORLD_CUP_GROUP_COPY,
  CREATE_WORLD_CUP_GROUP_DEFAULT_PAYOUT,
  CREATE_WORLD_CUP_GROUP_STEPS,
  type CrestColorId,
  type CrestEmblemId,
} from './CreateWorldCupGroupScreen.constants';
import {
  createWorldCupGroupDigitsOnly,
  createWorldCupGroupParsePct,
} from './createWorldCupGroupScreen.helper';

type Step = 1 | 2;

export const CreateWorldCupGroupScreen: React.FC = () => {
  const theme = useTheme();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  const styles = useMemo(
    () =>
      getCreateWorldCupGroupScreenStyles(theme, {
        contentBottomInset: 16,
      }),
    [theme],
  );
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { showAlert } = useDialog();

  const scrollRef = useRef<ScrollViewType>(null);
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const crestColor: CrestColorId = 'purple';
  const crestEmblem: CrestEmblemId = 'trophy';
  const [entryFeeCoins, setEntryFeeCoins] = useState(0);
  const [payoutMode, setPayoutMode] = useState<WorldCupPayoutMode>(WORLD_CUP_PAYOUT_MODE.WINNER_TAKE_ALL);
  const [pctFirst, setPctFirst] = useState(String(CREATE_WORLD_CUP_GROUP_DEFAULT_PAYOUT.FIRST));
  const [pctSecond, setPctSecond] = useState(String(CREATE_WORLD_CUP_GROUP_DEFAULT_PAYOUT.SECOND));
  const [pctThird, setPctThird] = useState(String(CREATE_WORLD_CUP_GROUP_DEFAULT_PAYOUT.THIRD));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChangeName = useCallback((text: string) => {
    setName(sanitizeInput(text, true).slice(0, GROUP_NAME_MAX_LENGTH));
  }, []);

  const handleChangeDescription = useCallback((text: string) => {
    setDescription(sanitizeInput(text, true).slice(0, WORLD_CUP_GROUP_DESCRIPTION_MAX_LENGTH));
  }, []);

  const handlePctFirstChange = useCallback((t: string) => {
    setPctFirst(createWorldCupGroupDigitsOnly(t, 3));
  }, []);
  const handlePctSecondChange = useCallback((t: string) => {
    setPctSecond(createWorldCupGroupDigitsOnly(t, 3));
  }, []);
  const handlePctThirdChange = useCallback((t: string) => {
    setPctThird(createWorldCupGroupDigitsOnly(t, 3));
  }, []);

  const p1 = createWorldCupGroupParsePct(pctFirst);
  const p2 = createWorldCupGroupParsePct(pctSecond);
  const p3 = createWorldCupGroupParsePct(pctThird);
  const pctSumValid = p1 + p2 + p3 === 100;
  const showPctError = payoutMode === WORLD_CUP_PAYOUT_MODE.TOP_THREE && !pctSumValid;

  const nameOk = name.trim().length >= GROUP_NAME_MIN_LENGTH && name.trim().length <= GROUP_NAME_MAX_LENGTH;
  const canGoNext = nameOk;
  const canSubmit =
    nameOk && !isSubmitting && (payoutMode === WORLD_CUP_PAYOUT_MODE.WINNER_TAKE_ALL || pctSumValid);
  const canProceed = step === 1 ? canGoNext : canSubmit;

  const handleBack = useCallback(() => {
    if (step === 2) { setStep(1); return; }
    router.back();
  }, [router, step]);

  const handleNext = useCallback(() => {
    if (step === 1) {
      setStep(2);
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    }
  }, [step]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const sanitizedName = sanitizeInput(name.trim(), true);
      const descSanitized = sanitizeInput(description.trim(), true).trim();
      const body = {
        name: sanitizedName,
        ...(descSanitized.length > 0 ? { description: descSanitized } : {}),
        crestColor,
        crestEmblem,
        entryFeeCoins: entryFeeCoins >= 0 ? entryFeeCoins : 0,
        payoutMode,
        ...(payoutMode === WORLD_CUP_PAYOUT_MODE.TOP_THREE
          ? { payoutFirstPct: p1, payoutSecondPct: p2, payoutThirdPct: p3 }
          : {}),
      };
      const result = await createWorldCupCompetitionGroup(apiClient, competitionId, body);
      void queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groups(competitionId),
      });
      router.replace({
        pathname: `${competitionGroupsRoute(basePath)}/created`,
        params: {
          groupId: result.id,
          inviteCode: result.inviteCode,
          groupName: result.name,
          crestColor,
          crestEmblem,
          description: descSanitized,
          entryFeeCoins: String(entryFeeCoins),
        },
      } as never);
    } catch (err) {
      if (!isRateLimitError(err)) {
        await showAlert({
          title: CREATE_WORLD_CUP_GROUP_COPY.ERROR_ALERT_TITLE,
          message: extractErrorMessage(err, 'Could not create squad. Try again.'),
          type: 'danger',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    apiClient, canSubmit, crestColor, crestEmblem, description,
    entryFeeCoins, name, p1, p2, p3, payoutMode, queryClient, router, showAlert,
  ]);

  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });
  const backdropGradientColors = useMemo(
    () => [`${theme.primaryDark}28`, theme.background.primary] as const,
    [theme],
  );
  const ctaGradientColors = useMemo(
    () => [theme.primaryDark, theme.primary] as const,
    [theme],
  );
  const ctaFadeColors = useMemo(
    () => ['transparent', theme.background.primary] as const,
    [theme],
  );
  const ctaLabel = step === 1
    ? CREATE_WORLD_CUP_GROUP_COPY.NEXT_STEP
    : isSubmitting
      ? CREATE_WORLD_CUP_GROUP_COPY.CREATING
      : CREATE_WORLD_CUP_GROUP_COPY.CREATE;
  const ctaSublabel = step === 1
    ? CREATE_WORLD_CUP_GROUP_COPY.NEXT_STEP_SUBLABEL
    : isSubmitting
      ? undefined
      : CREATE_WORLD_CUP_GROUP_COPY.CREATE_SUBLABEL;

  return (
    <View style={styles.container}>
      <LinearGradient
        pointerEvents="none"
        colors={backdropGradientColors}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 0.55 }}
        style={styles.gradientBackdrop}
      />
      <View style={styles.bokehLarge} pointerEvents="none" />
      <View style={styles.bokehSmall} pointerEvents="none" />
      <View style={styles.bokehAccent} pointerEvents="none" />

      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 6 : 0}
      >
        <View style={styles.inner}>
          {/* Header: [← Back] [progress] [step counter] + title + subtitle */}
          <View style={styles.header}>
            <View style={styles.navRow}>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.backButton,
                  hoverWeb,
                  pressed && styles.backButtonPressed,
                  hovered && Platform.OS === 'web' && styles.backButtonHover,
                ]}
                onPress={handleBack}
              >
                <ChevronLeft size={18} color={theme.primary} />
                <Text style={styles.backButtonText}>{CREATE_WORLD_CUP_GROUP_COPY.BACK}</Text>
              </Pressable>
              <View style={styles.progressTrack}>
                <View
                  style={[
                    styles.progressFill,
                    step === 1 ? styles.progressFillStep1 : styles.progressFillStep2,
                  ]}
                />
              </View>
              <Text style={styles.stepCounter}>
                {step} <Text style={styles.stepCounterTotal}>/ {CREATE_WORLD_CUP_GROUP_STEPS.TOTAL}</Text>
              </Text>
            </View>
            <Text style={styles.title}>
              {step === 1 ? CREATE_WORLD_CUP_GROUP_COPY.STEP_1_TITLE : CREATE_WORLD_CUP_GROUP_COPY.STEP_2_TITLE}
            </Text>
            <Text style={styles.subtitle}>
              {step === 1 ? CREATE_WORLD_CUP_GROUP_COPY.STEP_1_SUBTITLE : CREATE_WORLD_CUP_GROUP_COPY.STEP_2_SUBTITLE}
            </Text>
          </View>

          {/* Form content */}
          <ScrollView
            ref={scrollRef}
            style={styles.scrollArea}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {step === 1 ? (
              <CreateWorldCupGroupStep1Form
                theme={theme}
                name={name}
                description={description}
                isSubmitting={isSubmitting}
                onChangeName={handleChangeName}
                onChangeDescription={handleChangeDescription}
              />
            ) : (
              <CreateWorldCupGroupStep2Form
                theme={theme}
                entryFeeCoins={entryFeeCoins}
                onEntryFeeCoins={setEntryFeeCoins}
                payoutMode={payoutMode}
                onPayoutMode={setPayoutMode}
                pctFirst={pctFirst}
                pctSecond={pctSecond}
                pctThird={pctThird}
                onPctFirst={handlePctFirstChange}
                onPctSecond={handlePctSecondChange}
                onPctThird={handlePctThirdChange}
                isSubmitting={isSubmitting}
                showPctError={showPctError}
              />
            )}
          </ScrollView>

          {/* Sticky CTA */}
          <View pointerEvents="box-none" style={styles.stickyCtaWrap}>
            <LinearGradient
              pointerEvents="none"
              colors={ctaFadeColors}
              style={styles.stickyCtaFade}
            />
            <Pressable
              style={({ pressed }) => [
                styles.stickyCtaButton,
                !canProceed && styles.stickyCtaDisabled,
                pressed && canProceed && styles.stickyCtaPressed,
              ]}
              onPress={step === 1 ? handleNext : () => { void handleSubmit(); }}
              disabled={!canProceed || isSubmitting}
              testID={step === 1 ? 'create-world-cup-group-next-step' : 'create-world-cup-group-submit'}
            >
              <LinearGradient
                colors={ctaGradientColors}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.stickyCtaGradient}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color={theme.onPrimary} />
                ) : null}
                <Text style={styles.stickyCtaText}>{ctaLabel}</Text>
                {ctaSublabel ? (
                  <View style={styles.stickyCtaSublabelWrap}>
                    <Text style={styles.stickyCtaSublabelText}>{ctaSublabel}</Text>
                  </View>
                ) : null}
              </LinearGradient>
            </Pressable>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};
